import React, { useState } from 'react';
import { collection, addDoc, doc, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import Modal from './Modal';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import BlockTitle from './BlockTitle';
import useCompanies from '../lib/useCompanies';
import useProjectManagers from '../lib/useProjectManagers';
import './custom-bootstrap.scss';

const EMPTY_FORM = {
  name: '',
  location: '',
  manager: { name: '', phone: '' },
  managerUid: '',
  stages_total: 1,
  total_cost: 0,
  total_size: 0,
  company_id: '',
  contractsDriveUrl: '',
};

const AddInvestmentModal = ({ isOpen, onClose, onSave }) => {
  const { companies, loading: companiesLoading } = useCompanies();
  const { managers, loading: managersLoading } = useProjectManagers();
  const [investment, setInvestment] = useState(EMPTY_FORM);

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      const selectedCompany = companies.find((c) => c.id === investment.company_id) || null;
      const investmentData = {
        name: investment.name,
        location: investment.location,
        manager: investment.manager,
        managerUid: investment.managerUid || '',
        stages_total: parseInt(investment.stages_total) || 1,
        total_cost: parseFloat(investment.total_cost) || 0,
        total_size: parseFloat(investment.total_size) || 0,
        company_id: investment.company_id || '',
        company: selectedCompany
          ? { name: selectedCompany.name, nip: selectedCompany.nip, address: selectedCompany.address }
          : null,
        contractsDriveUrl: investment.contractsDriveUrl || '',
        status: 'active',
        current_stage: 0,
        completed: false,
        totalFlats: 0,
        soldFlats: 0,
        reservedFlats: 0,
      };

      const investmentRef = await addDoc(collection(firestore, 'investments'), investmentData);

      const remindersRef = doc(firestore, `investment_reminders/${investmentRef.id}`);
      await setDoc(remindersRef, {
        reminders: [
          { days: 7, enabled: true },
          { days: 14, enabled: true },
          { days: 21, enabled: false },
          { days: 30, enabled: true },
        ],
      });

      setInvestment(EMPTY_FORM);

      if (onSave) onSave(investmentRef.id);
      onClose();
    } catch (error) {
      console.error('Error creating investment:', error);
      alert('Nie udalo sie utworzyc inwestycji.');
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setInvestment((prev) => ({ ...prev, [name]: value }));
  };

  const onManagerSelect = (e) => {
    const uid = e.target.value;
    const pm = managers.find((m) => m.uid === uid);
    setInvestment((prev) => ({
      ...prev,
      managerUid: uid,
      manager: {
        ...prev.manager,
        name: pm ? pm.displayName : '',
      },
    }));
  };

  const onManagerPhoneChange = (e) => {
    const { value } = e.target;
    setInvestment((prev) => ({
      ...prev,
      manager: { ...prev.manager, phone: value },
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-8">
        <BlockTitle className="text-[22px]">
          Dodaj nowa inwestycje
        </BlockTitle>

        <form onSubmit={handleSave} className="grid grid-cols-2 gap-6 mt-6">
          {/* Investment Name */}
          <div className="col-span-2">
            <FormLabel htmlFor="name">Nazwa inwestycji</FormLabel>
            <FormInput
              id="name"
              name="name"
              value={investment.name}
              onChange={onChange}
              placeholder="Wprowadz nazwe inwestycji"
              required
            />
          </div>

          {/* Company */}
          <div className="col-span-2">
            <FormLabel htmlFor="company_id">Spolka</FormLabel>
            <select
              id="company_id"
              name="company_id"
              className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
              value={investment.company_id}
              onChange={onChange}
              disabled={companiesLoading}
              required
            >
              <option value="">
                {companiesLoading ? 'Wczytywanie spolek...' : '-- Wybierz spolke --'}
              </option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div className="col-span-2">
            <FormLabel htmlFor="location">Adres inwestycji</FormLabel>
            <FormInput
              id="location"
              name="location"
              value={investment.location}
              onChange={onChange}
              placeholder="Wprowadz adres inwestycji"
              required
            />
          </div>

          {/* Google Drive link */}
          <div className="col-span-2">
            <FormLabel htmlFor="contractsDriveUrl">Link do umów (Google Drive)</FormLabel>
            <FormInput
              id="contractsDriveUrl"
              name="contractsDriveUrl"
              type="url"
              value={investment.contractsDriveUrl}
              onChange={onChange}
              placeholder="https://drive.google.com/drive/folders/..."
            />
          </div>

          {/* Project Manager dropdown */}
          <div>
            <FormLabel htmlFor="managerUid">Kierownik projektu</FormLabel>
            <select
              id="managerUid"
              name="managerUid"
              className="w-full h-[38px] border border-[#D7D7D7] rounded-[6px] px-2 text-sm"
              value={investment.managerUid}
              onChange={onManagerSelect}
              disabled={managersLoading}
              required
            >
              <option value="">
                {managersLoading ? 'Wczytywanie...' : '-- Wybierz kierownika --'}
              </option>
              {managers.map((m) => (
                <option key={m.uid} value={m.uid}>{m.displayName}</option>
              ))}
            </select>
          </div>

          {/* Manager Phone (optional) */}
          <div>
            <FormLabel htmlFor="managerPhone">Numer telefonu (opcjonalnie)</FormLabel>
            <FormInput
              id="managerPhone"
              name="phone"
              value={investment.manager.phone}
              onChange={onManagerPhoneChange}
              placeholder="Wprowadz numer telefonu"
            />
          </div>

          {/* Total Stages */}
          <div>
            <FormLabel htmlFor="stages_total">Liczba etapow budowy</FormLabel>
            <FormInput
              id="stages_total"
              name="stages_total"
              type="number"
              min="1"
              value={investment.stages_total}
              onChange={onChange}
              placeholder="Wprowadz liczbe etapow"
              required
            />
          </div>

          {/* Investment Cost */}
          <div>
            <FormLabel htmlFor="total_cost">Umowa z GW (PLN)</FormLabel>
            <FormInput
              id="total_cost"
              name="total_cost"
              type="number"
              value={investment.total_cost || ''}
              onChange={onChange}
              placeholder="Wprowadź wartość umowy z GW"
            />
          </div>

          {/* Total Size */}
          <div className="col-span-2">
            <FormLabel htmlFor="total_size">Powierzchnia całkowita (m²)</FormLabel>
            <FormInput
              id="total_size"
              name="total_size"
              type="number"
              step="0.01"
              value={investment.total_size || ''}
              onChange={onChange}
              placeholder="Wprowadź powierzchnię całkowitą"
            />
          </div>

          {/* Buttons */}
          <div className="col-span-2 flex justify-end mt-10">
            <Button type="submit" variant="default">
              Dodaj
            </Button>
            <Button type="button" variant="bordered" onClick={onClose} className="ml-4">
              Anuluj
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddInvestmentModal;
